import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPublicDecks, copyPublicDeck } from '../services/api';
import { toast } from 'react-hot-toast';
import { Globe, Copy, BookOpen, User } from 'lucide-react';

export default function Explore() {
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState(null);
  const [search, setSearch] = useState('');
  //const navigate = useNavigate();

  useEffect(() => {
    loadPublicDecks();
  }, []);

  const loadPublicDecks = async () => {
    try {
      const r = await getPublicDecks();
      setDecks(r.data);
    } catch {
      toast.error('Failed to load public decks');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (deck) => {
    setCopying(deck.id);
    try {
      await copyPublicDeck(deck.id);
      toast.success(`"${deck.title}" copied to your decks ✓`);
    } catch {
      toast.error('Failed to copy deck');
    } finally {
      setCopying(null);
    }
  };

  const filtered = decks.filter(d =>
    d.title.toLowerCase().includes(search.toLowerCase()) ||
    (d.description || '').toLowerCase().includes(search.toLowerCase()) ||
    (d.owner_username || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ padding: '2rem', maxWidth: 800, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.4rem' }}>
          <Globe size={20} color="var(--accent)" />
          <h1 style={{ fontSize: '1.3rem', fontWeight: 700 }}>Explore Public Decks</h1>
        </div>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          Browse decks shared by the community — copy any deck to start practicing.
        </p>
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Search decks or users..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="form-input"
        style={{ marginBottom: '1.5rem', width: '100%' }}
      />

      {/* Content */}
      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Loading...</p>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-faint)' }}>
          <Globe size={32} style={{ marginBottom: '1rem', opacity: 0.3 }} />
          <p style={{ fontSize: '0.85rem' }}>No public decks found.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filtered.map(deck => (
            <div key={deck.id} style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '1.1rem 1.25rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem'
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem' }}>
                  <span style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text)' }}>
                    {deck.title}
                  </span>
                </div>
                {deck.description && (
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                    {deck.description}
                  </p>
                )}
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-faint)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <BookOpen size={11} /> {deck.flashcard_count || 0} cards
                  </span>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-faint)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <User size={11} /> {deck.owner_username}
                  </span>
                </div>
              </div>
              <button
                className="btn btn-secondary"
                style={{ whiteSpace: 'nowrap', padding: '0.5rem 0.9rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 6 }}
                onClick={() => handleCopy(deck)}
                disabled={copying === deck.id}
              >
                <Copy size={12} />
                {copying === deck.id ? 'Copying…' : 'Copy deck'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}